import { questionTypesWithColsSelect } from '@webapp/common/lib/forward-logic';
import { replaceMask } from '@webapp/common/lib/ui';
import { AnswerType, QuestionType } from '@webapp/common/resources/survey';

import type { AnswersRequest } from '@webapp/survey/src/api/queries-api';
import { getApiClass } from '@webapp/survey/src/api/queries-api';
import type { QuestionActorRef, QuestionContext } from '@webapp/survey/src/machine/question-machine';

import type { QuestionAnswerActor } from '../../entities/answer-machine';

export const replaceMaxSize = (s: string, v: string): string => replaceMask({ max: v }, s);
export const replaceFileExt = (s: string, v: string): string => replaceMask({ formats: v }, s);
export const replaceMaxCount = (s: string, v: number): string => replaceMask({ count: v }, s);

type ISavedQuestion = Pick<
    QuestionContext,
    'id' | 'answersActors' | 'groupsActors' | 'responseActor' | 'dirty' | 'expired'
>;

export const mapQuestionForSnapshot = (actor: QuestionActorRef): ISavedQuestion => {
    const { answersActors, dirty, expired, groupsActors, id, responseActor } = actor.getSnapshot().context;

    return {
        id,
        answersActors,
        groupsActors,
        responseActor,
        expired,
        dirty,
    };
};

export const mapQuestionForQueue = (q: QuestionActorRef): AnswersRequest => {
    const api = getApiClass(q.getSnapshot().context.type);
    return api.query(q);
};

export const hideCarryForwardAnswers = (
    questionActors: Array<QuestionActorRef>,
    onSetHidden: (actor: QuestionAnswerActor, value: boolean) => void,
) => {
    questionActors.forEach((qActor) => {
        const q = qActor.getSnapshot().context;
        if (q.forwardLogic.length > 0) {
            const answerActorById = q.answersActors.reduce((acc, item) => {
                acc[item.getSnapshot().context.id] = item;
                return acc;
            }, {} as Record<number, QuestionAnswerActor>);

            q.forwardLogic.forEach((forwardLogic) => {
                const childQ = questionActors.find((q) => q.getSnapshot().context.id === forwardLogic.childQuestionId);

                if (!childQ) return;

                let answeredIds: Array<number> = [];
                let notAnsweredIds: Array<number> = [];

                if (questionTypesWithColsSelect.has(q.type)) {
                    answeredIds = q.answersActors
                        .filter((a) => {
                            const answer = a.getSnapshot().context;
                            if (q.type === QuestionType.MATRIX_RATING) {
                                return forwardLogic.details?.some(
                                    (detail) =>
                                        'numericValue' in detail &&
                                        answer.response?.getSnapshot().context.value === detail.numericValue,
                                );
                            }
                            const detailsGroupIds = forwardLogic.details
                                .filter((detail) => 'groupId' in detail && typeof detail.groupId === 'number')
                                .map((d) => (d as { groupId: number }).groupId);
                            const groupActors = q.groupsActors.filter((g) =>
                                detailsGroupIds.includes(g.getSnapshot().context.id),
                            );
                            const isGroupSelected = groupActors.some(
                                (group) =>
                                    group.getSnapshot().context.responses[answer.id]?.getSnapshot().context.value,
                            );
                            return isGroupSelected;
                        })
                        .map((a) => a.getSnapshot().context.id);
                    notAnsweredIds = q.answersActors
                        .filter((a) => {
                            const answer = a.getSnapshot().context;
                            if (q.type === QuestionType.MATRIX_RATING) {
                                return !forwardLogic.details?.some(
                                    (detail) =>
                                        'numericValue' in detail &&
                                        answer.response?.getSnapshot().context.value === detail.numericValue,
                                );
                            }
                            const detailsGroupIds = forwardLogic.details
                                .filter((detail) => 'groupId' in detail && typeof detail.groupId === 'number')
                                .map((d) => (d as { groupId: number }).groupId);
                            const groupActors = q.groupsActors.filter((g) =>
                                detailsGroupIds.includes(g.getSnapshot().context.id),
                            );
                            const isGroupSelected = groupActors.some(
                                (group) =>
                                    group.getSnapshot().context.responses[answer.id]?.getSnapshot().context.value,
                            );
                            return !isGroupSelected;
                        })
                        .map((a) => a.getSnapshot().context.id);
                } else {
                    answeredIds = q.answersActors
                        .filter((a) => a.getSnapshot().context.response?.getSnapshot().context.value)
                        .map((a) => a.getSnapshot().context.id);
                    notAnsweredIds = q.answersActors
                        .filter((a) => !a.getSnapshot().context.response?.getSnapshot().context.value)
                        .map((a) => a.getSnapshot().context.id);
                }

                if (forwardLogic.type === 'category' && childQ.getSnapshot().context.categories?.length > 0) {
                    childQ.getSnapshot().context.categories.forEach((category) => {
                        const forwardParent = category.parentAnswerId;

                        if (notAnsweredIds.includes(forwardParent)) {
                            category.hidden = Boolean(forwardLogic.checked);
                        }
                        if (answeredIds.includes(forwardParent)) {
                            category.hidden = !forwardLogic.checked;
                        }
                    });

                    return;
                }

                childQ.getSnapshot().context.answersActors.forEach((a) => {
                    const forwardParent = a.getSnapshot().context.forwardParent;

                    if (
                        answerActorById[forwardParent]?.getSnapshot().context.type === AnswerType.SELF &&
                        !forwardLogic.checked
                    ) {
                        onSetHidden(a, true);
                        return;
                    }
                    if (notAnsweredIds.includes(forwardParent)) {
                        onSetHidden(
                            a,
                            forwardLogic.checked
                                ? true
                                : !!answerActorById[forwardParent]?.getSnapshot().context.hidden,
                        );
                    }
                    if (answeredIds.includes(forwardParent)) {
                        onSetHidden(
                            a,
                            forwardLogic.checked
                                ? !!answerActorById[forwardParent]?.getSnapshot().context.hidden
                                : true,
                        );
                    }
                });
            });
        }
    });
};
